import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SideBar from '../../components/Topbar';
import { HTMLCourseNavProvider } from "../../components/SidebarCourseContext";
import './assets/styles/HTMLLesson1.css';
import { CopyBlock, dracula } from "react-code-blocks";

// Import sophisticated icons
import { 
  FaCode,
  FaCheck,
  FaArrowRight,
  FaPlay,
  FaBookOpen,
  FaLayerGroup,
  FaBold,
  FaItalic,
  FaTextHeight,
  FaHighlighter,
  FaStrikethrough,
  FaUnderline,
  FaExclamationTriangle,
  FaSubscript,
  FaSuperscript,
  FaEye
} from 'react-icons/fa';

const HTMLLesson18 = () => {
  const [activeSection, setActiveSection] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [viewedSections, setViewedSections] = useState(new Set([0]));
  const sectionRefs = useRef([]);
  const observerRef = useRef(null);

  const [language] = useState("html");
  const [lineNumbers] = useState(true);

  // Video explanation URL
  const videoUrl = "https://app.codeflarelimited.com/watch-video/html-course";

  const code1 = `<!-- Bold text (presentational) -->
<p>This is <b>bold text</b> for visual emphasis.</p>

<!-- Strong importance (semantic) -->
<p>This is <strong>very important information</strong> that requires attention.</p>

<!-- Usage examples -->
<h3>Product Features:</h3>
<ul>
  <li><b>Fast</b> processing speeds</li>
  <li><b>Easy</b> to use interface</li>
  <li><strong>Warning:</strong> Requires secure connection</li>
  <li><strong>Critical:</strong> Backup your data regularly</li>
</ul>`;

  const code2 = `<!-- Italic text (presentational) -->
<p>This is <i>italic text</i> for stylistic variation.</p>

<!-- Emphasized text (semantic) -->
<p>I <em>really</em> need you to understand this point.</p>

<!-- Different use cases -->
<p>
  The term <i>HTML</i> stands for HyperText Markup Language.<br>
  She said, <i>"I'll be there by 8 PM."</i><br>
  This is <em>extremely important</em> for security.<br>
  You <em>must</em> complete this step before proceeding.
</p>

<!-- Technical terms and phrases -->
<p>
  The <i>var</i> keyword in JavaScript is function-scoped.<br>
  <i>Homo sapiens</i> is the scientific name for humans.<br>
  The <i>Mona Lisa</i> is displayed in the Louvre.
</p>`;

  const code3 = `<!-- Small text for disclaimers -->
<p>Regular text <small>with smaller disclaimer text</small></p>

<!-- Legal and copyright information -->
<footer>
  <small>&copy; 2024 Company Name. All rights reserved.</small>
</footer>

<!-- Side comments and fine print -->
<p>
  Product price: $99.99<br>
  <small>Plus applicable taxes and shipping</small>
</p>

<!-- Inline small text -->
<h2>Special Offer <small>Limited Time</small></h2>
<p>Get this amazing deal <small>while supplies last</small></p>`;

  const code4 = `<!-- Highlighted text -->
<p>This sentence contains <mark>important highlighted text</mark>.</p>

<!-- Search results and key terms -->
<p>
  Search results for "HTML":<br>
  Learn <mark>HTML</mark> basics in our comprehensive course.<br>
  Advanced <mark>HTML</mark> techniques for professionals.
</p>

<!-- Study notes and references -->
<blockquote>
  The key principles of web development include:<br>
  - <mark>Semantic HTML</mark> for structure<br>
  - <mark>CSS</mark> for presentation<br>
  - <mark>JavaScript</mark> for behavior
</blockquote>`;

  const code5 = `<!-- Deleted text -->
<p>Original price: <del>$199.99</del></p>
<p>My favorite color was <del>blue</del> but now it's green.</p>

<!-- Inserted text -->
<p>My favorite <ins>color</ins> is red.</p>
<p>Meeting scheduled for <del>Monday</del> <ins>Tuesday</ins>.</p>

<!-- Combined usage -->
<article>
  <h3>Document Revisions</h3>
  <p>
    Version 1.0: The quick brown fox <del>jumps</del> <ins>leaps</ins> over the lazy dog.<br>
    Version 1.1: Added <ins>new security features</ins> and removed <del>deprecated methods</del>.
  </p>
</article>

<!-- Editorial changes -->
<p>
  The project deadline is <del>December 15</del> <ins>December 20</ins>.<br>
  Team members: John, Sarah, <del>Mike</del>, <ins>Emily</ins>.
</p>`;

  const code6 = `<!-- Subscript for formulas -->
<p>Water chemical formula: H<sub>2</sub>O</p>
<p>Carbon dioxide: CO<sub>2</sub></p>

<!-- Superscript for exponents -->
<p>Mathematical equation: x<sup>2</sup> + y<sup>2</sup> = z<sup>2</sup></p>
<p>10<sup>3</sup> = 1000</p>

<!-- Combined usage -->
<p>
  Einstein's equation: E = mc<sup>2</sup><br>
  Glucose formula: C<sub>6</sub>H<sub>12</sub>O<sub>6</sub><br>
  Quadratic formula: x = [-b ± √(b<sup>2</sup> - 4ac)] / 2a
</p>

<!-- Footnotes and references -->
<p>
  This research builds on previous work<sup>1,2</sup>.<br>
  The element was discovered in 1869<sub>†</sub>.
</p>`;

  const code7 = `<!DOCTYPE html>
<html>
<head>
  <style>
    /* Modern formatting styles */
    .formatting-demo {
      max-width: 800px;
      margin: 2rem auto;
      padding: 2rem;
      background: white;
      border-radius: 12px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    }
    
    .format-section {
      margin: 2rem 0;
      padding: 1.5rem;
      background: #f8fafc;
      border-radius: 8px;
      border-left: 4px solid #3b82f6;
    }
    
    /* Enhanced formatting styles */
    strong {
      color: #dc2626;
      font-weight: 700;
    }
    
    em {
      color: #7c3aed;
      font-style: italic;
    }
    
    mark {
      background: linear-gradient(120deg, #fef3c7 0%, #fef3c7 100%);
      padding: 0.1rem 0.3rem;
      border-radius: 3px;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    }
    
    del {
      color: #ef4444;
      text-decoration: line-through;
      background: #fef2f2;
      padding: 0.1rem 0.2rem;
    }
    
    ins {
      color: #059669;
      text-decoration: underline;
      background: #f0fdf4;
      padding: 0.1rem 0.2rem;
    }
    
    small {
      color: #6b7280;
      font-size: 0.875em;
    }
    
    sub, sup {
      font-size: 0.75em;
      line-height: 0;
    }
    
    sub {
      color: #3b82f6;
    }
    
    sup {
      color: #d97706;
    }
    
    /* Pricing example */
    .pricing {
      background: white;
      padding: 1.5rem;
      border-radius: 8px;
      border: 2px solid #e5e7eb;
      margin: 1rem 0;
    }
    
    .original-price {
      color: #6b7280;
    }
    
    .current-price {
      font-size: 1.5rem;
      font-weight: bold;
      color: #059669;
    }
    
    /* Scientific example */
    .scientific {
      background: #f0f9ff;
      padding: 1.5rem;
      border-radius: 8px;
      border-left: 4px solid #0ea5e9;
      font-family: 'Courier New', monospace;
    }
    
    /* Legal example */
    .legal {
      background: #fffbeb;
      padding: 1.5rem;
      border-radius: 8px;
      border: 1px solid #fef3c7;
    }
  </style>
</head>
<body>
  <div class="formatting-demo">
    <h1>HTML Text Formatting Implementation Guide</h1>
    
    <!-- Semantic vs Presentational -->
    <section class="format-section">
      <h2>Semantic vs Presentational Formatting</h2>
      
      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 2rem;">
        <div>
          <h3>Semantic Elements</h3>
          <p><strong>Important:</strong> Use for meaningful emphasis</p>
          <p><em>Emphasized:</em> Adds linguistic stress</p>
          <p>Both convey meaning beyond just appearance</p>
        </div>
        
        <div>
          <h3>Presentational Elements</h3>
          <p><b>Bold:</b> Pure visual styling</p>
          <p><i>Italic:</i> Stylistic variation only</p>
          <p>Use when semantics aren't needed</p>
        </div>
      </div>
    </section>
    
    <!-- Practical Usage Examples -->
    <section class="format-section">
      <h2>Practical Formatting Examples</h2>
      
      <!-- Pricing Example -->
      <div class="pricing">
        <h3>Special Offer</h3>
        <p>
          <span class="original-price"><del>$199.99</del></span><br>
          <span class="current-price">Now only $149.99!</span><br>
          <small>Limited time offer. Taxes additional.</small>
        </p>
        <p><strong>Hurry!</strong> This deal ends <mark>soon</mark>.</p>
      </div>
      
      <!-- Scientific Example -->
      <div class="scientific">
        <h3>Scientific Notation</h3>
        <p>
          Chemical equations:<br>
          - Water: H<sub>2</sub>O<br>
          - Carbon dioxide: CO<sub>2</sub><br>
          - Glucose: C<sub>6</sub>H<sub>12</sub>O<sub>6</sub>
        </p>
        <p>
          Mathematical formulas:<br>
          - Einstein: E = mc<sup>2</sup><br>
          - Quadratic: x = [-b ± √(b<sup>2</sup> - 4ac)] / 2a<br>
          - Area of circle: πr<sup>2</sup>
        </p>
      </div>
      
      <!-- Document Revision Example -->
      <div>
        <h3>Document Revision History</h3>
        <p>
          Version 2.1 Changes:<br>
          - Added <ins>new security protocols</ins><br>
          - Removed <del>deprecated API endpoints</del><br>
          - <em>Emphasized</em> the new requirements<br>
          - <strong>Critical:</strong> Backup must be performed
        </p>
      </div>
    </section>
    
    <!-- Accessibility Section -->
    <section class="format-section">
      <h2>Accessibility Best Practices</h2>
      
      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 1.5rem;">
        <div>
          <h4>✅ Do This</h4>
          <ul>
            <li>Use <strong>strong</strong> for important content</li>
            <li>Use <em>em</em> for linguistic emphasis</li>
            <li>Pair visual formatting with semantics</li>
            <li>Test with screen readers</li>
          </ul>
        </div>
        
        <div>
          <h4>❌ Avoid This</h4>
          <ul>
            <li>Using formatting for decoration only</li>
            <li>Overusing bold/italic text</li>
            <li>Relying solely on color or formatting</li>
            <li>Ignoring semantic meaning</li>
          </ul>
        </div>
      </div>
    </section>
    
    <!-- Real-world Application -->
    <section class="format-section">
      <h2>Real-world Application</h2>
      
      <div class="legal">
        <h3>Legal Document Example</h3>
        <p>
          This <strong>Agreement</strong> is made between <em>Company Name</em> and the User.<br>
          User <em>must</em> comply with all terms outlined herein.<br>
          <small>
            This document is legally binding. Consult with legal counsel before signing.<br>
            &copy; 2024 Legal Firm Inc. All rights reserved.
          </small>
        </p>
      </div>
      
      <!-- Technical Documentation -->
      <div style="margin-top: 2rem;">
        <h3>Technical Documentation</h3>
        <p>
          The <code>function</code> keyword <del>was deprecated</del> <ins>is still supported</ins>.<br>
          Use <mark>async/await</mark> for better readability.<br>
          <strong>Warning:</strong> This method may throw an error.<br>
          Time complexity: O(n<sup>2</sup>) - consider optimization.
        </p>
      </div>
    </section>
  </div>
</body>
</html>`;

  const sections = [
    {
      title: "HTML Text Formatting Introduction",
      content: "HTML provides semantic and presentational elements to format text for better readability, emphasis, and meaning",
      keyPoints: [
        "Semantic elements convey meaning (strong, em)",
        "Presentational elements style text (b, i)",
        "Accessibility benefits with proper semantic use",
        "Multiple formatting options for different contexts"
      ],
      icon: <FaTextHeight className="section-icon" />,
      color: "#2563eb",
      code: code1
    },
    {
      title: "Bold vs Strong Elements", 
      content: "Understand the difference between visual bold styling and semantic strong importance for accessibility",
      keyPoints: [
        "<b> for visual bold styling without importance",
        "<strong> for semantic importance and emphasis",
        "Screen readers may emphasize strong content",
        "Use strong for critical information and warnings"
      ],
      icon: <FaBold className="section-icon" />,
      color: "#dc2626",
      code: code1
    },
    {
      title: "Italic vs Emphasis Elements",
      content: "Distinguish between stylistic italics and semantic emphasis for proper content meaning",
      keyPoints: [
        "<i> for visual italic styling",
        "<em> for semantic emphasis and stress",
        "Use i for technical terms, phrases, and titles",
        "Use em for linguistic emphasis in sentences"
      ],
      icon: <FaItalic className="section-icon" />,
      color: "#7c3aed",
      code: code2
    },
    {
      title: "Small and Marked Text",
      content: "Use small text for disclaimers and marked text for highlighting important content",
      keyPoints: [
        "<small> for fine print and legal text",
        "<mark> for highlighted or marked content",
        "Perfect for search results and key terms",
        "Common in legal documents and footnotes"
      ],
      icon: <FaHighlighter className="section-icon" />,
      color: "#d97706",
      code: code4
    },
    {
      title: "Deleted and Inserted Text",
      content: "Show document revisions and changes with deleted and inserted text elements",
      keyPoints: [
        "<del> for removed or deleted content",
        "<ins> for added or inserted content",
        "Perfect for version control and edits",
        "Common in pricing, schedules, and documentation"
      ],
      icon: <FaStrikethrough className="section-icon" />,
      color: "#059669",
      code: code5
    },
    {
      title: "Subscript and Superscript",
      content: "Format mathematical formulas, scientific notation, and references with subscript and superscript",
      keyPoints: [
        "<sub> for subscript (lowered text)",
        "<sup> for superscript (raised text)",
        "Essential for mathematical equations",
        "Used in chemical formulas and footnotes"
      ],
      icon: <FaSuperscript className="section-icon" />,
      color: "#0891b2",
      code: code6
    }
  ];

  // Intersection Observer for scroll detection
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '-10% 0px -20% 0px',
      threshold: 0.6
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const index = sectionRefs.current.indexOf(entry.target);
        
        if (entry.isIntersecting) {
          setActiveSection(index);
          setViewedSections(prev => new Set([...prev, index]));
        }
      });
    }, options);

    sectionRefs.current.forEach((section) => {
      if (section) {
        observerRef.current.observe(section);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const addSectionRef = (element, index) => {
    sectionRefs.current[index] = element;
  };

  const getCompletionPercentage = () => {
    const totalSections = sections.length + 3;
    const viewedCount = viewedSections.size;
    return Math.round((viewedCount / totalSections) * 100);
  };

  return (
    <HTMLCourseNavProvider>
      <div className="html-lesson-container">
        <SideBar />
        
        <div className="html-lesson-main">
          {/* Header Section */}
          <div className="html-lesson-header">
            <div className="html-header-content">
              <div className="html-lesson-badge">
                <FaBookOpen className="html-badge-icon" />
                Lesson 18 • {getCompletionPercentage()}% Viewed
              </div>
              <h1 className="html-lesson-title">HTML Text Formatting Mastery</h1>
              <p className="html-lesson-subtitle">
                Master semantic and presentational text formatting for accessible, meaningful content
              </p>
              
              {/* Progress Bar */}
              <div className="html-completion-progress">
                <div 
                  className="html-completion-fill"
                  style={{ width: `${getCompletionPercentage()}%` }}
                ></div>
                <div className="html-progress-labels">
                  <span>Getting Started</span>
                  <span>Complete</span>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Indicator */}
          <div className="html-progress-indicator">
            {sections.map((section, index) => (
              <div 
                key={index}
                className={`html-progress-step ${viewedSections.has(index) ? 'html-viewed' : ''} ${index === activeSection ? 'html-active' : ''}`}
              >
                <div className="html-step-number">
                  {viewedSections.has(index) ? '✓' : index + 1}
                </div>
                <div className="html-step-label">{section.title.split(' ')[0]}</div>
                {index < sections.length - 1 && <div className="html-step-connector"></div>}
              </div>
            ))}
          </div>

          {/* Visual Formatting Demonstration */}
          <div 
            className="html-visual-class"
            ref={(el) => addSectionRef(el, 0)}
          >
            <div className="html-visual-header">
              <div className="html-code-badge">
                <FaLayerGroup className="html-badge-icon" />
                Text Formatting Power
              </div>
              <h3 className="html-visual-title">
                Semantic and Presentational Text Formatting for Every Use Case
              </h3>
            </div>
            
            <div className="html-class-demo">
              <div className="html-demo-code">
                <h4>Formatting Syntax Foundation</h4>
                <div className="html-code-block-small">
                  <CopyBlock
                    language="html"
                    text={`<!-- Semantic -->
<strong>Important text</strong>
<em>Emphasized text</em>

<!-- Presentational -->
<b>Bold text</b>
<i>Italic text</i>

<!-- Special formatting -->
<mark>Highlighted</mark>
<del>Deleted</del>
<ins>Inserted</ins>
<sub>Subscript</sub>
<sup>Superscript</sup>`}
                    showLineNumbers={false}
                    theme={dracula}
                    wrapLines={true}
                    codeBlock
                  />
                </div>
              </div>
              
              <div className="html-demo-visual">
                <h4>Live Formatting Examples</h4>
                <div className="html-format-preview">
                  <div className="format-demo-item">
                    <p>This is <strong>important</strong> text</p>
                    <span>Strong emphasis</span>
                  </div>
                  <div className="format-demo-item">
                    <p>This is <em>emphasized</em> text</p>
                    <span>Semantic stress</span>
                  </div>
                  <div className="format-demo-item">
                    <p>This is <mark>highlighted</mark> text</p>
                    <span>Marked content</span>
                  </div>
                  <div className="format-demo-item">
                    <p>H<sub>2</sub>O and x<sup>2</sup></p>
                    <span>Sub/Superscript</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="html-demo-note">
              <FaExclamationTriangle className="html-note-icon" />
              <p><strong>Remember:</strong> Always prefer semantic elements (strong, em) over presentational ones (b, i) for better accessibility!</p>
            </div>
          </div>

          {/* Content Sections */}
          <div className="html-content-sections">
            {sections.map((section, index) => (
              <div 
                key={index}
                ref={(el) => addSectionRef(el, index + 1)}
                className={`html-content-card ${viewedSections.has(index + 1) ? 'html-viewed' : ''} ${(index + 1) === activeSection ? 'html-active' : ''}`}
              >
                <div className="html-card-badge">
                  {viewedSections.has(index + 1) ? '✓ Viewed' : `Part ${index + 1}`}
                </div>
                
                <div className="html-card-header">
                  <div className="html-card-icon" style={{ backgroundColor: section.color }}>
                    {section.icon}
                  </div>
                  <div className="html-card-text">
                    <h2 className="html-card-title">{section.title}</h2>
                    <p className="html-card-content">{section.content}</p>
                    
                    <div className="html-key-points">
                      <h4>Key Points:</h4>
                      <ul>
                        {section.keyPoints.map((point, idx) => (
                          <li key={idx}>{point}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Code Example */}
                {section.code && (
                  <div className="html-section-code">
                    <div className="html-code-badge">
                      <FaCode className="html-badge-icon" />
                      Example
                    </div>
                    <div className="html-code-block-small">
                      <CopyBlock
                        language={language}
                        text={section.code}
                        showLineNumbers={lineNumbers}
                        theme={dracula}
                        wrapLines={true}
                        codeBlock
                      />
                    </div>
                  </div>
                )}
                
                <div className="html-card-actions">
                  <a 
                    href={videoUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="html-action-btn"
                  >
                    <FaPlay className="html-btn-icon" />
                    Watch Video Explanation
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Formatting Elements Comparison */}
          <div 
            className="html-comparison-section"
            ref={(el) => addSectionRef(el, sections.length + 1)}
          >
            <div className="html-comparison-header">
              <div className="html-code-badge">
                <FaEye className="html-badge-icon" />
                Formatting Elements Guide
              </div>
              <h3 className="html-comparison-title">
                Semantic vs Presentational Formatting Elements
              </h3>
            </div>
            
            <div className="html-comparison-table">
              <table>
                <thead>
                  <tr>
                    <th>Element</th>
                    <th>Type</th>
                    <th>Purpose</th>
                    <th>Accessibility</th>
                    <th>Best For</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>&lt;strong&gt;</code></td>
                    <td>Semantic</td>
                    <td>Important content</td>
                    <td>Screen readers emphasize</td>
                    <td>Warnings, critical info</td>
                  </tr>
                  <tr>
                    <td><code>&lt;b&gt;</code></td>
                    <td>Presentational</td>
                    <td>Visual bold styling</td>
                    <td>No special treatment</td>
                    <td>Keywords, product names</td>
                  </tr>
                  <tr>
                    <td><code>&lt;em&gt;</code></td>
                    <td>Semantic</td>
                    <td>Emphasized content</td>
                    <td>Screen readers stress</td>
                    <td>Linguistic emphasis</td>
                  </tr>
                  <tr>
                    <td><code>&lt;i&gt;</code></td>
                    <td>Presentational</td>
                    <td>Visual italic styling</td>
                    <td>No special treatment</td>
                    <td>Technical terms, titles</td>
                  </tr>
                  <tr>
                    <td><code>&lt;mark&gt;</code></td>
                    <td>Semantic</td>
                    <td>Highlighted content</td>
                    <td>Context dependent</td>
                    <td>Search results, key terms</td>
                  </tr>
                  <tr>
                    <td><code>&lt;del&gt;/&lt;ins&gt;</code></td>
                    <td>Semantic</td>
                    <td>Document changes</td>
                    <td>Indicates modifications</td>
                    <td>Revisions, edits</td>
                  </tr>
                  <tr>
                    <td><code>&lt;sub&gt;/&lt;sup&gt;</code></td>
                    <td>Presentational</td>
                    <td>Position formatting</td>
                    <td>Reads normally</td>
                    <td>Formulas, footnotes</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Formatting Best Practices */}
          <div 
            className="html-practices-section"
            ref={(el) => addSectionRef(el, sections.length + 2)}
          >
            <div className="html-practices-header">
              <div className="html-code-badge">
                <FaCheck className="html-badge-icon" />
                Formatting Best Practices
              </div>
              <h3 className="html-practices-title">
                Professional Text Formatting Guidelines
              </h3>
            </div>
            
            <div className="html-practices-grid">
              <div className="html-practice-card html-do">
                <h4>✅ Do These</h4>
                <ul>
                  <li>Use semantic elements for meaningful content</li>
                  <li>Prefer <code>strong</code> over <code>b</code> for importance</li>
                  <li>Use <code>em</code> for linguistic emphasis</li>
                  <li>Test formatting with screen readers</li>
                  <li>Use formatting consistently throughout</li>
                </ul>
              </div>
              
              <div className="html-practice-card html-dont">
                <h4>❌ Avoid These</h4>
                <ul>
                  <li>Using formatting for pure decoration</li>
                  <li>Overusing bold or italic text</li>
                  <li>Mixing semantic and presentational arbitrarily</li>
                  <li>Relying solely on formatting for meaning</li>
                  <li>Ignoring accessibility considerations</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Use Case Examples */}
          <div 
            className="html-multiple-classes"
            ref={(el) => addSectionRef(el, sections.length + 3)}
          >
            <div className="html-multiple-header">
              <div className="html-code-badge">
                <FaTextHeight className="html-badge-icon" />
                Practical Use Cases
              </div>
              <h3 className="html-multiple-title">
                Real-world Formatting Applications
              </h3>
            </div>
            
            <div className="html-multiple-content">
              <div className="html-multiple-explanation">
                <h4>Common Formatting Scenarios:</h4>
                <ul>
                  <li><strong>Legal Documents:</strong> Use <code>strong</code> for important clauses and <code>small</code> for disclaimers</li>
                  <li><strong>Academic Papers:</strong> Use <code>em</code> for emphasis and <code>sub/sup</code> for formulas</li>
                  <li><strong>E-commerce:</strong> Use <code>del</code> for original prices and <code>ins</code> for sale prices</li>
                  <li><strong>Technical Docs:</strong> Use <code>mark</code> for key terms and <code>code</code> for examples</li>
                  <li><strong>News Articles:</strong> Use <code>strong</code> for key facts and <code>em</code> for quotes</li>
                </ul>
              </div>
              
              <div className="html-use-case-examples">
                <h4>Quick Reference Examples:</h4>
                <div className="use-case-grid">
                  <div className="use-case-item">
                    <div className="use-case-icon">⚖️</div>
                    <span>Legal: Strong & Small</span>
                  </div>
                  <div className="use-case-item">
                    <div className="use-case-icon">🔬</div>
                    <span>Scientific: Sub & Sup</span>
                  </div>
                  <div className="use-case-item">
                    <div className="use-case-icon">🛒</div>
                    <span>E-commerce: Del & Ins</span>
                  </div>
                  <div className="use-case-item">
                    <div className="use-case-icon">📚</div>
                    <span>Academic: Em & Mark</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Comprehensive Example */}
          <div 
            className="html-code-example"
            ref={(el) => addSectionRef(el, sections.length + 4)}
          >
            <div className="html-code-header">
              <div className="html-code-badge">
                <FaCode className="html-badge-icon" />
                Complete Example
              </div>
              <h3 className="html-code-title">
                Professional Formatting System Implementation
              </h3>
            </div>
            <div className="html-code-block">
              <CopyBlock
                language={language}
                text={code7}
                showLineNumbers={lineNumbers}
                theme={dracula}
                wrapLines={true}
                codeBlock
              />
            </div>
            <div className="html-code-actions">
              <a 
                href={videoUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="html-code-btn html-primary"
              >
                Watch Complete Formatting Implementation Guide
              </a>
            </div>
          </div>

          {/* Action Section */}
          <div 
            className="html-action-section"
            ref={(el) => addSectionRef(el, sections.length + 5)}
          >
            <div className="html-action-header">
              <h3>Mastered HTML text formatting?</h3>
              <div className="html-completion-badge">
                {getCompletionPercentage()}% of content viewed
              </div>
            </div>
            
            <div className="html-action-content">
              <p>You've learned comprehensive text formatting including semantic vs presentational elements, accessibility considerations, and practical implementation across various use cases. Start creating meaningful, accessible text content!</p>
              
              <div className="html-action-buttons">
                <button 
                  className={`html-complete-button ${isCompleted ? 'html-completed' : ''}`}
                  onClick={() => setIsCompleted(true)}
                >
                  {isCompleted ? (
                    <>
                      <FaCheck className="html-button-icon" />
                      Lesson Completed
                    </>
                  ) : (
                    'Mark as Complete'
                  )}
                </button>
                
                {isCompleted && (
                  <Link to="/dashboard" className="html-next-lesson-link">
                    Complete Course
                    <FaArrowRight className="html-link-icon" />
                  </Link>
                )}
              </div>
              
              <div className="html-quick-nav">
                <span>Need more examples? </span>
                <a 
                  href={videoUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="html-nav-link"
                >
                  Watch Complete HTML Course
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </HTMLCourseNavProvider>
  );
}

export default HTMLLesson18;